import { useMemo } from 'react';
import { Alert, Container, Grid, Group, Loader, Stack } from '@mantine/core';
import { AlertCircle } from 'react-feather';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { FullContentHeader } from '../../../components/layout/FullContentHeader';
import { SessionCompleteBadge } from '../../../components/session/SessionCompleteBadge';
import { useFeatureFlag } from '../../../stores/featureFlags';
import { useAuthStore } from '../../../stores/auth';
import { usePeerLeaderPostSession } from '../../../hooks/usePeerLeaderPostSession';
import { useRecapDraft } from '../../../hooks/useRecapDraft';
import {
  peerLeaderPostSessionApi,
  type PeerLeaderPostSessionBundle,
} from '../../../services/api/peerLeaderPostSession';
import RecapEmailDraftCard from '../../coach/components/post-session/RecapEmailDraftCard';
import DeleteTranscriptControl from '../../coach/components/post-session/DeleteTranscriptControl';
import SessionThemesCard from '../../coach/components/post-session/SessionThemesCard';
import InSessionInsightsCard from '../../coach/components/post-session/InSessionInsightsCard';
import LogCommitmentsCard from '../../coach/components/post-session/LogCommitmentsCard';
import SessionNotesCard from '../../coach/components/post-session/SessionNotesCard';
import PeerLeaderAttendanceCard from './PeerLeaderAttendanceCard';
import PeerLeaderFeedbackFormCard from './PeerLeaderFeedbackFormCard';
import NextSessionCallout from './NextSessionCallout';

/**
 * MED-817 / E4.5 — Peer Leader Post-Session screen.
 *
 * Route: `/participant/cohorts/:cohortId/sessions/:sessionId/post-session`
 * (participant navigation, not coach routes — AC2). Entered automatically on
 * End Session in E4.4 (AC1). Gated by `live_session_delivery`.
 *
 * A role-aware delta on the coach post-session screen (MED-794). Two-column
 * layout (AC3):
 *   Left/main (wider):  Attendance & Engagement → Medley Feedback Form →
 *                       Log Commitments → Your Session Notes
 *   Right (narrower):   Recap Email Draft → Next Session Callout (AC14)
 *
 * The peer leader appears in their own participant list with a "You" badge in
 * Attendance (AC5) and Log Commitments (AC8). AI features (pre-population,
 * clean up, generation) are deferred to E4.6.
 */
export interface PeerLeaderPostSessionPageProps {
  /**
   * MED-1040 — DEV-only static preview: seeds the bundle and bypasses the
   * feature-flag gate + fetch so the full page renders from a fixture
   * (`/dev/post-session/peer`). Compiled out of production builds.
   */
  preview?: { bundle: PeerLeaderPostSessionBundle };
}

export function PeerLeaderPostSessionPage({
  preview,
}: PeerLeaderPostSessionPageProps = {}) {
  const isPreview = !!preview;
  const navigate = useNavigate();
  const params = useParams<{ cohortId: string; sessionId: string }>();
  const cohortId = params.cohortId ?? preview?.bundle.session.cohort_id;
  const sessionId = params.sessionId ?? preview?.bundle.session.id;
  const liveSessionDeliveryEnabled = useFeatureFlag('live_session_delivery');
  const currentUser = useAuthStore(s => s.user);

  const {
    bundle,
    loading,
    error,
    refresh,
    attendance,
    setAttendanceForParticipant,
    saveAttendance,
    savingAttendance,
    feedbackResponses,
    setFeedbackResponse,
    saveFeedbackDraft,
    submitFeedback,
    savingFeedback,
    feedbackSubmittedAt,
    commitments,
    saveCommitment,
    savingParticipantId,
  } = usePeerLeaderPostSession(sessionId ?? null, {
    previewBundle: preview?.bundle,
  });

  const aiFeaturesEnabled = bundle?.ai_features_enabled === true;
  // MED-1128 — post-session AI (recap) requires BOTH modality AI AND the session's
  // listening state. When listening was off there is no transcript, so an AI recap
  // would be ungrounded: treat it exactly like the modality-off case (template, no
  // AI seed) and surface an inline note. Mirrors CoachPostSessionPage.
  const aiListeningEnabled = bundle?.ai_listening_enabled === true;
  const recapAiAvailable = aiFeaturesEnabled && aiListeningEnabled;
  const aiListeningOffForSession = aiFeaturesEnabled && !aiListeningEnabled;
  const { envelope: recapDraftEnvelope } = useRecapDraft(
    sessionId ?? null,
    liveSessionDeliveryEnabled && recapAiAvailable
  );

  const leaderName = useMemo(() => {
    if (!currentUser) return 'You';
    const full =
      `${currentUser.first_name ?? ''} ${currentUser.last_name ?? ''}`.trim();
    return full || currentUser.email || 'You';
  }, [currentUser]);

  // MED-1133 (E4.5a) — Log Commitments is SELF-ONLY for the peer leader: a peer
  // cannot see another peer's commitment. The shared LogCommitmentsCard renders one
  // row per participant, so we feed it just the leader's own row (the backend already
  // delivers only the leader's commitment — this keeps the UI consistent and means
  // the shared card + coach screen stay untouched, AC5). Attendance/Notes/Recap keep
  // the full roster.
  const leaderOwnParticipants = useMemo(
    () =>
      bundle
        ? bundle.participants.filter(p => p.user_id === bundle.leader_user_id)
        : [],
    [bundle]
  );

  if (!sessionId || !cohortId) {
    return <Navigate to="/participant" replace />;
  }

  // MED-1181 — defense-in-depth: disable when the global flag is off OR the
  // per-cohort LSD modality is off on the returned bundle (backend already
  // hard-blocks a non-LSD cohort). Mirrors the five sibling participant routes.
  if (
    !isPreview &&
    (!liveSessionDeliveryEnabled || bundle?.live_session_delivery_enabled === false)
  ) {
    return (
      <Container size="md" py="xl">
        <Alert color="yellow" icon={<AlertCircle size={16} />}>
          Live session delivery is not enabled for your account.
        </Alert>
      </Container>
    );
  }

  if (loading && !bundle) {
    return (
      <Container size="xl" py="xl">
        <Group justify="center">
          <Loader />
        </Group>
      </Container>
    );
  }

  if (error || !bundle) {
    return (
      <Container size="md" py="xl">
        <Alert
          color="red"
          icon={<AlertCircle size={16} />}
          title="Could not load post-session"
        >
          {error ?? 'Session not found.'}
        </Alert>
      </Container>
    );
  }

  const { session, participants } = bundle;

  return (
    <>
      <FullContentHeader
        data-testid="post-session-header"
        title={session.title || 'Session Complete'}
        programName={session.cohort_name}
        clientName={session.group_name}
        durationMinutes={session.duration_minutes}
        onBack={() => navigate('/participant')}
        sticky
        accessory={<SessionCompleteBadge />}
      />
      <Container size="xl" py="lg">
        <Stack gap="lg">
          <Grid gutter="lg">
            {/* Left/main column — wider (AC3 order) */}
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Stack gap="md">
                <PeerLeaderAttendanceCard
                  participants={participants}
                  attendance={attendance}
                  leaderUserId={bundle.leader_user_id}
                  onParticipantChange={setAttendanceForParticipant}
                  onSave={saveAttendance}
                  saving={savingAttendance}
                />

                <PeerLeaderFeedbackFormCard
                  responses={feedbackResponses}
                  onChange={setFeedbackResponse}
                  onSaveDraft={() => void saveFeedbackDraft()}
                  onSubmit={() => void submitFeedback()}
                  saving={savingFeedback}
                  submittedAt={feedbackSubmittedAt}
                />

                <LogCommitmentsCard
                  participants={leaderOwnParticipants}
                  commitments={commitments}
                  onSave={saveCommitment}
                  savingParticipantId={savingParticipantId}
                  leaderUserId={bundle.leader_user_id}
                />

                <SessionNotesCard
                  sessionId={sessionId}
                  initialHtml={session.coach_session_notes_html ?? ''}
                  initialUpdatedAt={session.coach_session_notes_updated_at}
                  onSaved={refresh}
                  collapsible
                />
              </Stack>
            </Grid.Col>

            {/* Right column — narrower */}
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Stack gap="md">
                <RecapEmailDraftCard
                  sessionId={sessionId}
                  participants={participants}
                  groupName={session.group_name}
                  cohortName={session.cohort_name ?? ''}
                  sessionNumber={session.session_number}
                  sessionDateIso={session.scheduled_start}
                  topicName={session.topic_name ?? ''}
                  coachName={leaderName}
                  recapDraftEnvelope={recapDraftEnvelope}
                  nextSessionDateIso={bundle.next_session_date}
                  defaultTemplate={bundle.recap_email_default_template}
                  aiFeaturesEnabled={recapAiAvailable}
                  aiListeningOffForSession={aiListeningOffForSession}
                  sendFn={peerLeaderPostSessionApi.sendRecapEmail}
                  savedDraftEdit={bundle.recap_draft_edit}
                  saveDraftFn={peerLeaderPostSessionApi.saveRecapDraftEdit}
                />
                {/* MED-1194 (B10) — in-session insights (aggregate airtime tier
                  only on this view). MED-1207 (D16): the standalone Meeting
                  Summary and the peer-leader Airtime Balance chart are retired
                  here — the coach keeps the chart, peers keep only the airtime
                  feedback text. With the Airtime Balance anchor gone, this card
                  now sits at the top of the report cluster, above Discussion
                  Themes (AC7). */}
                <InSessionInsightsCard
                  insights={bundle.insession_insights}
                  aiAvailable={recapAiAvailable}
                />
                {/* MED-1193 (B9) — themes, plain register, below the in-session
                  insights card (Airtime Balance retired on this view, MED-1207). */}
                <SessionThemesCard
                  themes={bundle.themes}
                  aiAvailable={recapAiAvailable}
                  viewerRegister="plain"
                />
                {/* MED-1187 (AC8) — leader parity for the manual transcript delete. */}
                <DeleteTranscriptControl
                  sessionId={sessionId}
                  hasTranscript={bundle.has_transcript}
                />

                {bundle.next_session_callout && (
                  <NextSessionCallout
                    sessionId={bundle.next_session_callout.session_id}
                    sessionNumber={bundle.next_session_callout.session_number}
                    sessionDateIso={bundle.next_session_callout.session_date}
                  />
                )}
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      </Container>
    </>
  );
}

export default PeerLeaderPostSessionPage;
